import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePutOrdersOrderIdParams, TypePutOrdersOrderIdResponse } from '~/types/delivery-agency.types';

/**
 * [배송 주문 관리] 배송 주문 수정(수령장소/출입방법) API
 *
 * 주문 상태가 출고 대기/출고 진행중일 때만 수정 가능하며, 4개 필드 전체를 덮어쓴다.
 * (같은 출고번호로 묶인 형제 주문에도 함께 반영된다)
 */
const fetchPutOrdersOrderId = async ({
  orderId,
  ...body
}: TypePutOrdersOrderIdParams): Promise<TypePutOrdersOrderIdResponse['data']> => {
  const { data } = await axiosInstance.put<TypePutOrdersOrderIdResponse>(
    `${API_ENDPOINT.DELIVERY_AGENCY.PUT_ORDER}/${orderId}`,
    body
  );
  return data.data;
};

/**
 * [배송 주문 관리] 배송 주문 수정 Mutation Hook
 * @description 에러 처리는 사용처에서 개별 처리 (팝업 공통 코드 61000은 다이얼로그, 그 외 토스트)
 */
export const usePutOrdersOrderIdMutation = () => {
  return useMutation<TypePutOrdersOrderIdResponse['data'], TypeErrorResponseData['error'], TypePutOrdersOrderIdParams>({
    mutationFn: fetchPutOrdersOrderId,
    meta: { showGlobalErrorToast: false },
  });
};

/**
 * [배송 주문 관리] 배송 주문 수정 Mock
 */
registerMockAPI<TypePutOrdersOrderIdResponse | TypeErrorResponseData>({
  method: 'put',
  endpoint: '/api-delivery-agency/orders/:id(\\d+)',
  label: '배송 주문 관리/주문 수정',
  scenarios: [
    {
      name: '성공',
      response: { data: null },
      delay: 400,
    },
    {
      name: '수정 불가 상태 (60000)',
      status: 400,
      response: {
        error: {
          code: 60000,
          message: '수령장소 및 출입방법은 주문 상태가 출고 대기 또는 출고 진행중인 경우에만 수정 가능합니다: 배송중',
        },
      },
    },
    {
      name: '수령장소 상세 누락 (60000)',
      status: 400,
      response: {
        error: {
          code: 60000,
          message: "수령장소가 '택배 수령실' 또는 '기타'인 경우 수령장소 상세를 입력해야 합니다.",
        },
      },
    },
    {
      name: '이모지 포함 (60400)',
      status: 400,
      response: {
        error: {
          code: 60400,
          message: '이모지는 허용되지 않습니다.',
        },
      },
    },
    {
      name: '주문 없음 (60404)',
      status: 404,
      response: {
        error: {
          code: 60404,
          message: '요청한 리소스를 찾을 수 없습니다.',
        },
      },
    },
    {
      name: '팝업 공통 에러 (61000)',
      status: 400,
      response: {
        error: {
          code: 61000,
          message: '주문 수정 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
        },
      },
    },
  ],
});
